import { useEffect, useRef, useState } from "react";
import * as React from "react";
import classNames from "clsx";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useSnapshot } from "valtio";
import { letterStore } from "src/js/stores/letter/store";
import { useActiveSiteBoundaryImageUrl } from "react-migration/domains/sites/hooks/useActiveSiteBoundaryImageUrl";
import { Placeholder } from "./LetterParts/Placeholder";
import { SecondPageContent } from "./LetterParts/SecondPageContent";
import { Signature } from "./LetterParts/Signature";
import { Footer } from "./LetterParts/Footer";

interface PageProps {
  onHeightChange?: (isLong: boolean) => void;
  showValues?: boolean;
}

const MAX_HEIGHT = 1123;

export const SecondPage: React.FC<PageProps> = ({ onHeightChange, showValues }) => {
  const { t } = useTranslation();
  const usAccess = hasFeature(Feature.usAccess);
  const {
    letter: { bodyTextPage2, signaturePage, showSiteBoundary },
  } = useSnapshot(letterStore);
  const [height, setHeight] = useState<number>();
  const letterPage = useRef<HTMLDivElement>(null);
  const { siteBoundaryImageUrl } = useActiveSiteBoundaryImageUrl();

  useEffect(() => {
    const newHeight = letterPage.current?.clientHeight;
    if (newHeight && newHeight !== height) {
      setHeight(newHeight);
      onHeightChange?.(newHeight > MAX_HEIGHT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyTextPage2, signaturePage, showSiteBoundary, height]);

  const satelliteImageClassNames = [
    "flex",
    "flex-col",
    "items-center",
    "justify-center",
    "h-[330px]",
    "w-[420px]",
    "m-auto",
  ];

  return (
    <div
      className={classNames(
        "mt-8",
        "bg-white",
        "mx-auto",
        "drop-shadow-md",
        "font-['Arial']",
        "text-[11pt]",
        "leading-[15pt]",
        usAccess ? "p-[0.68in]" : "p-[20mm]",
        !usAccess && "pl-[22mm]",
        usAccess ? "min-h-letter-height" : "min-h-a4-height",
        usAccess ? "w-letter-width" : "w-a4-width"
      )}
      data-testid="letter-preview-second-page"
      ref={letterPage}
    >
      {showSiteBoundary && (
        <>
          {showValues && siteBoundaryImageUrl ? (
            <div className={classNames(satelliteImageClassNames)}>
              <img className={classNames("h-auto", "w-full", "object-contain")} src={siteBoundaryImageUrl} />
            </div>
          ) : (
            <Placeholder className={classNames(satelliteImageClassNames)}>
              <div className={classNames("flex", "flex-col", "items-center", "justify-center")}>
                <i className="text-3xl icon-lt-image-line text-neutral-400" />
                {t("sites.letters.satellite_image")}
              </div>
            </Placeholder>
          )}
        </>
      )}
      {bodyTextPage2 && <SecondPageContent />}
      {signaturePage === 2 && <Signature showValues={showValues} />}
      <Footer />
    </div>
  );
};
